<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import { FormLabel, FormInput } from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import { computed, reactive, ref, toRefs } from "vue";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import useVuelidate from "@vuelidate/core";
import { useUsuariosStore } from "../../../../services/usuarios";
import { useMensajesStore } from "../../../../services/mensajes";
import Alert from "../../../../base-components/Alert/Alert.vue";
import { LogAcciones, Usuario } from "@fixeduy/restaurantestypes";
import { getCurrentTime } from "../../../../utils/fecha";
import { userUserStore } from "../../../../stores/user";
import { useLogAccionesStore } from "../../../../services/logAcciones";

interface Props {
  usuario: Usuario;
}

const props = defineProps<Props>();
const mensajesStore = useMensajesStore();
const usuariosStore = useUsuariosStore();
const userStore = userUserStore();
const logAccionesStore = useLogAccionesStore();
const cargando = ref(false);

const emit = defineEmits<{ (e: "cerrar"): void }>();

const formData = reactive({
  pin: ""
});

const rules = computed(() => ({
  pin: {
    requerido: {
      $validator: (v: string) => v.length === 4,
      $message: "El PIN debe tener exactamente 4 dígitos."
    },
    soloNumeros: {
      $validator: (v: string) => /^\d{4}$/.test(v) || v.length === 0,
      $message: "El PIN debe contener solo 4 dígitos numéricos."
    }
  }
}));

const validate = useVuelidate(rules, toRefs(formData));

const handleEdit = async () => {
  const result = await validate.value.$validate();

  if (result) {
    cargando.value = true;
    try {
      const res = await usuariosStore.establecerPin(
        props.usuario,
        formData.pin
      );
      if (res.exito) {
        const logAcciones: LogAcciones = {
          id: "",
          accion: [
            `Se establece pin a usuario ${props.usuario.nombre} (${props.usuario.id}).`
          ],
          modulo: "usuarios",
          fecha: getCurrentTime(),
          usuario: userStore.userLoggedIn?.uid!,
          mail: userStore.userLoggedIn?.email!
        };

        await logAccionesStore.guardarLog(logAcciones);
        mensajesStore.mostrarToast(
          "PIN establecido",
          "PIN de turnos configurado correctamente",
          mensajesStore.mensajeType.exito
        );
        await usuariosStore.getFromFirestore();
        emit("cerrar");
      } else {
        mensajesStore.mostrarModal(
          "Error estableciendo PIN",
          res.errorMsg ||
            "No se pudo guardar el PIN. Revisá que las Cloud Functions estén desplegadas en este entorno (Testing).",
          mensajesStore.mensajeType.error
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      mensajesStore.mostrarModal(
        "Error al llamar al servidor",
        msg +
          "\n\nComprobá que las Cloud Functions estén desplegadas en este proyecto (npm run deployTesting en RestaurantesCloudFunctions).",
        mensajesStore.mensajeType.error
      );
    } finally {
      cargando.value = false;
    }
  }
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="lg">
    <Dialog.Panel>
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Establecer PIN para turnos
        </h2>
      </Dialog.Title>
      <!-------------------- DIALOG DESCRIPTION ------------------------>
      <Dialog.Description>
        <div class="flex">
          <div class="flex-none w-50 pr-5 mt-2">
            <FormLabel htmlFor="pin-usuario"> Usuario</FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormInput
              type="text"
              disabled="true"
              :value="props.usuario.nombre"
            />
          </div>
        </div>
        <div class="mt-5 text-warning">
          <Alert>
            El PIN de 4 dígitos es usado para marcaje de turnos (ingreso,
            descanso, salida). Es distinto a la contraseña de login.
          </Alert>
        </div>
        <div class="flex mt-5">
          <div class="flex-none w-50 pr-5 mt-2 text-right">
            <FormLabel htmlFor="horizontal-form-pin" class="w-40 sw:w-20">
              PIN (4 dígitos)
            </FormLabel>
          </div>
          <div class="flex-1">
            <FormInput
              id="horizontal-form-pin"
              type="text"
              inputmode="numeric"
              maxlength="4"
              pattern="[0-9]*"
              v-model.trim="formData.pin"
              placeholder="0000"
              @keypress="
                (e: KeyboardEvent) => !/^\d$/.test(e.key) && e.preventDefault()
              "
            />
          </div>
        </div>
        <template v-if="validate.pin.$error">
          <div
            v-for="(error, index) in validate.pin.$errors"
            :key="index"
            class="mt-2 mb-2 text-danger"
          >
            {{ error.$message }}
          </div>
        </template>
      </Dialog.Description>

      <!-------------------- DIALOG FOOTER ------------------------>
      <Dialog.Footer>
        <div v-if="cargando">
          <div
            class="flex flex-col items-rigth justify-end col-span-6 sm:col-span-3 xl:col-span-2"
          >
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
        <div v-else class="flex justify-between">
          <!-------------------- Botón Cerrar ------------------------>
          <Button
            variant="secondary"
            class="mb-2 mr-3"
            @click="() => emit('cerrar')"
          >
            Cerrar
          </Button>
          <!-------------------- Botón Guardar ------------------------>
          <Button variant="primary" class="mb-2 mr-1" @click="handleEdit">
            Establecer PIN
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
